import { GTW_STATUS } from 'utils/constants';
import { Dispatch, SetStateAction, ComponentType } from 'react';

// skipcq: JS-0337
export enum ConfigurationInputType {
  TEXT = 'text',
  PASSWORD = 'password',
  SELECT = 'select',
  TOGGLE = 'toggle',
  Amount = 'amount',
  TIPPING_TOGGLE = 'tippingToggle',
  HIDDEN = 'hidden',
}

export enum PaymentDeviceConfig {
  FULL = 'full',
  SIMPLE = 'simple',
  NONE = 'none',
}

export enum PaymentSolutionTab {
  ATM = 'ATM',
  CREDIT = 'Credit',
  DEBIT = 'Debit',
  HOME = 'Home',
  ACH = 'ACH',
}

export enum ModalType {
  EDIT = 'edit',
  SAVE = 'save',
}

export enum TabVisibilityKey {
  ATM = 'showAtm',
  CREDIT = 'showCredit',
  DEBIT = 'showDebit',
  ACH = 'showAch',
}

export type TabVisibility = {
  [TabVisibilityKey.ATM]: boolean;
  [TabVisibilityKey.CREDIT]: boolean;
  [TabVisibilityKey.DEBIT]: boolean;
  [TabVisibilityKey.ACH]: boolean;
};

export const VISIBILITY_KEY_MAP: Record<string, TabVisibilityKey> = {
  [PaymentSolutionTab.ATM]: TabVisibilityKey.ATM,
  [PaymentSolutionTab.CREDIT]: TabVisibilityKey.CREDIT,
  [PaymentSolutionTab.DEBIT]: TabVisibilityKey.DEBIT,
  [PaymentSolutionTab.ACH]: TabVisibilityKey.ACH,
};

export const PAYMENT_TYPE_MAP: Record<PaymentSolutionTab, string> = {
  [PaymentSolutionTab.ATM]: 'ATM',
  [PaymentSolutionTab.DEBIT]: 'Debit',
  [PaymentSolutionTab.CREDIT]: 'Credit',
  [PaymentSolutionTab.ACH]: 'ACH',
  [PaymentSolutionTab.HOME]: '',
};

export namespace PAYMENT_CONFIG {
  interface ErrorObject {
    loc: Array<string>;
    msg: string;
    type: string;
  }

  export interface Error {
    fetchPaymentSolutions?: {
      message?: string;
    };
    fetchDefaultProvider?: {
      message?: string;
    };
    getPaymentDevice?: {
      message?: string;
    };
    createPaymentDevice?: {
      message?: string;
    };
    editPaymentDevice?: {
      message?: string;
    };
    deviceStatus?: {
      message?: string;
    };
    deviceDeleted?: {
      message?: string;
    };
    getCardReaderValues?: {
      message?: string;
    };
    saveCardReader?: {
      message?: string;
    };
    saveDefaultCardReader?: {
      message?: string;
    };
    paymentConfiguration?: {
      message?: string;
    };
    getPosLocation?: {
      message?: string;
    };
    getMsoPermissionList?: {
      message?: string;
    };
    fetchChannelConfig?: {
      message?: string;
    };
    updateChannelConfig?: {
      message?: string;
    };
    fetchDefaultConfigurations?: {
      message?: string;
    };
    createDefaultConfiguration?: {
      message?: string;
    };
    updatePaymentSolutions?: {
      message?: string;
    };
    fetchComponentSdkConfig?: {
      message?: string;
    };
    updateComponentSdkConfig?: {
      message?: string;
    };
  }

  export interface IComponentSdkConfigValue {
    iframe: boolean;
    enableCfdQR: boolean;
    enableStaticQR: boolean;
    enableLoyaltyRewards: boolean;
    [key: string]: boolean;
  }

  export interface IComponentSdkConfig {
    id: string;
    namespace: string;
    scope: string;
    component: string;
    entityId: string | null;
    channel: string;
    schemaVersion: number;
    schemaId: string;
    value: IComponentSdkConfigValue;
    rowVersion: number;
    createdAt: string;
    updatedAt: string;
  }

  export interface IFetchComponentSdkConfigParams {
    component: string;
    entityId: string;
    namespace: string;
    channel: string;
  }

  export interface IUpdateComponentSdkConfigParams {
    component: string;
    entityId: string;
    namespace: string;
    channel: string;
    schemaVersion: number;
    value: Record<string, boolean>;
  }

  export interface IProcessors {
    processorId: string;
    isEnabled: boolean;
    processorName: string;
    paymentMethod?: string;
  }

  export interface DefaultProcessorData {
    processorId: string;
    processorName: string;
    paymentType: string;
  }
  export interface IFetchDefaultProcessor {
    message: string;
    data: Array<DefaultProcessorData | null>;
  }

  export interface IFetchAllProcessors {
    message: string;
    data: Array<IProcessors | null>;
  }

  export type ICreatePaymentDeviceSimple = {
    name: string;
    deviceregisterid: string;
    processorId: string;
    posLocationId?: string;
    locationId?: string;
  };

  export type ICreatePaymentDeviceFull = {
    name: string;
    deviceregisterid: string;
    printConfig: string | null;
    deviceauthcode: string | null;
    processorId: string;
    tpn?: string;
    posLocationId?: string;
    locationId?: string;
  };
  export interface IGetPaymentDeviceLocation {
    id: string;
    name: string;
    deviceregisterid: string;
    createdAt: string;
    processorId: string;
    processorName: string;
    updatedAt: string;
    deviceauthcode: string;
    printConfig: string;
    tpn: string;
    posLocationId?: string;
    locationId?: string;
  }

  export type PaymentDeviceRequestBody = {
    processorNames: string;
    processorId: string;
  };

  export interface IProcessorObject {
    processors: Array<IProcessors> | null;
  }

  export interface IPaymentSolutions {
    entityId: string;
    processor: IProcessorObject;
  }

  export interface IUpdatePaymentSolutions {
    entityId: string;
    processor: any;
  }

  export interface IUpdatePaymentSolutionsAPIResponse {
    message: string;
    data: {
      entityId: string;
      processor: {
        processors: {
          processorId: string;
          processorName: string;
          isEnabled: boolean;
        }[];
      };
      webhookValidated?: boolean;
    };
    errorRaw: any;
  }

  export interface IEntityPermissionObject {
    id: string;
    permissions: string[];
  }

  export interface IMSOPermissionsList {
    entityContexts: IEntityPermissionObject[];
  }

  export type IGlobalCardReader = {
    name: string;
    category: string | null;
    code: string;
    value: string;
    id: string | undefined;
    isSecret: boolean;
    isRequired: boolean;
    processorId?: string;
  };
  export type IGlobalCardReaderDefault = {
    name: string;
    category: string;
    processorId: string;
    code: string;
    value: string;
    isSecret: boolean;
    isRequired: boolean;
  };

  export interface IDefaultConfiguration {
    name: string;
    category: string;
    processorId: string;
    code: string;
    value: string;
    isSecret: boolean;
    isRequired: boolean;
  }

  export interface IDefaultConfigurationItem extends IDefaultConfiguration {
    id?: string;
  }

  export interface IFetchDefaultConfigurations {
    message: string;
    data: IDefaultConfigurationItem[];
  }

  export interface IGetCardReaderValues {
    category: string;
    code: string;
    createdAt: string;
    entityId: string;
    id: string;
    isRequired: boolean;
    isSecret: boolean;
    name: string;
    processorId: string;
    value: string;
  }
  export interface IGetCardReaderList {
    items: IGetCardReaderValues[];
  }
  export interface IDeviceStatus {
    isOnline: boolean;
    raw?: string;
  }

  export type IChannelKey =
    | 'ecommerce'
    | 'hosted_payments'
    | 'ffd'
    | 'virtual_terminal'
    | 'kiosk'
    | 'pos';
  export type IFlowKey =
    | 'payment_link_redirect'
    | 'payment_link_sms'
    | 'payment_link_qr'
    | 'terminal_sale';

  export interface IChannelFlowConfig {
    channel: IChannelKey;
    flows: IFlowKey[];
  }

  export interface IChannelFlowConfigNullable
    extends Omit<IChannelFlowConfig, 'flows'> {
    flows: IFlowKey[] | null;
  }

  export interface IProcessorConfigurationObj {
    configurations: IProcessorConfiguration[];
    defaultConfigurations: IProcessorConfiguration[];
    deviceConfigurationMode: PaymentDeviceConfig;
    id: string;
    defaultChannelConfig: IChannelFlowConfig[];
  }
  export interface ISelectedOptions {
    name: string;
    value: string;
  }

  export interface IInputOptions {
    percentValuesCode?: string;
  }
  export interface IProcessorConfiguration {
    displayName: string;
    inputType: ConfigurationInputType;
    isSecret: boolean;
    code: string;
    isRequired: boolean;
    defaultValue?: string;
    selectOptions?: ISelectedOptions[];
    description?: string;
    helpText?: string;
    category?: string;
    inputOptions?: IInputOptions;
  }
  export interface IPosLocation {
    id: string;
    name: string;
    type: string;
  }

  export interface IUpdateChannelConfigRequest {
    processorId: string;
    channelFlows: IChannelFlowConfigNullable[];
  }

  export type IChannelConfiguration = IChannelFlowConfigNullable[];

  export interface PaymentConfigReducer {
    isLoading: number;
    error: Error;
    paymentSolutions: IPaymentSolutions;
    allPaymentSolutions: IFetchAllProcessors;
    defaultProcessor: IFetchDefaultProcessor;
    defaultConfigurations: IFetchDefaultConfigurations;
    filteredList: IProcessors[] | undefined;
    paymentDeviceList: IGetPaymentDeviceLocation[] | undefined;
    paymentDeviceCreated: boolean;
    paymentDeviceUpdated: boolean;
    deviceStatus: IDeviceStatus;
    deviceDeleted: boolean;
    getCardReaderValues: IGetCardReaderList;
    saveCardReader: boolean;
    saveDefaultCardReader: boolean;
    createDefaultConfig: boolean;
    paymentConfiguration: IProcessorConfigurationObj;
    posLocationList: IPosLocation[] | undefined;
    paymentStatus: GTW_STATUS;
    msoPermissionsList: IMSOPermissionsList | undefined;
    channelConfiguration: IChannelConfiguration | undefined;
    componentSdkConfig: Record<string, IComponentSdkConfig> | undefined;
    componentSdkConfigUpdated: boolean;
  }
}

// Props for payment config tab components - setActiveTab is optional as only Home needs it
export type TabComponentProps = {
  activeTab: PaymentSolutionTab;
  defaultProvider: PAYMENT_CONFIG.IProcessors | undefined;
  setDefaultProvider: Dispatch<
    SetStateAction<PAYMENT_CONFIG.IProcessors | undefined>
  >;
  setActiveTab?: Dispatch<SetStateAction<PaymentSolutionTab>>;
};

export interface TabConfig {
  value: PaymentSolutionTab;
  label: string;
  component: ComponentType<TabComponentProps>;
  showKey?: TabVisibilityKey;
}
